/**
 * The search field over a list of enquiry lines — one implementation, so the RFQ builder's table
 * and the record-quotation dialog look and behave identically.
 *
 * ---- What the design is doing ----
 *
 * The magnifier sits INSIDE the field rather than beside it, so the control reads as a search box
 * at a glance instead of a text input someone labelled. The clear button appears only once there
 * is something to clear — a permanent × on an empty field is a control that does nothing.
 *
 * The count is the important part and is deliberately not a caption underneath: "12 of 79" beside
 * the box is what tells a purchaser the list they are looking at is a SUBSET. Without it a filtered
 * table is indistinguishable from a short enquiry, which is how somebody concludes an item is
 * missing. It turns warn-coloured on zero matches, because an empty table with no explanation is
 * the one state that reads as broken.
 */

import type { CSSProperties } from 'react'

export function SearchBox({
  value,
  onChange,
  total,
  shown,
  placeholder,
  style,
}: {
  value: string
  onChange: (next: string) => void
  /** Rows before filtering — the denominator, and what the resting placeholder counts. */
  total: number
  /** Rows after filtering. */
  shown: number
  placeholder?: string
  style?: CSSProperties
}) {
  const active = value.trim() !== ''
  const empty = active && shown === 0

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', ...style }}>
      {/*
        Sized to its job, not to the page. A filter is a secondary control sitting above the thing
        it filters — stretched across the full width it competes with the bulk bar for the eye, and
        reads as the primary action on the screen, which it is not.
      */}
      <div style={{ position: 'relative', width: 260 }}>
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: 9,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 12,
            opacity: 0.5,
            pointerEvents: 'none',
          }}
        >
          🔍
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          /* Short enough to READ. The long form ("description, item code or Cust. S.No") was
             truncated mid-word in the field, which tells the purchaser less than nothing. */
          placeholder={placeholder ?? 'Search items…'}
          aria-label="Search lines"
          title="Search by description, item code or customer S.No"
          style={{
            width: '100%',
            padding: '6px 26px 6px 27px',
            border: `1px solid ${active ? 'var(--brand)' : 'var(--line)'}`,
            borderRadius: 8,
            background: 'var(--panel)',
            color: 'var(--ink)',
            fontSize: 12.5,
            fontFamily: 'inherit',
          }}
        />
        {active ? (
          <button
            type="button"
            aria-label="Clear search"
            title="Clear search"
            onClick={() => onChange('')}
            style={{
              position: 'absolute',
              right: 4,
              top: '50%',
              transform: 'translateY(-50%)',
              border: 0,
              background: 'transparent',
              color: 'var(--muted)',
              cursor: 'pointer',
              fontSize: 14,
              lineHeight: 1,
              padding: 3,
            }}
          >
            ×
          </button>
        ) : null}
      </div>

      {/*
        The count, and ONLY while filtering. This is the part that matters: "12 of 79" is what says
        the list below is a subset — without it a filtered table is indistinguishable from a short
        enquiry, which is how somebody concludes an item has gone missing. Warn-toned on zero,
        because an empty table with no explanation is the one state that reads as broken.
      */}
      {active ? (
        <span style={{ fontSize: 12, fontWeight: 600, color: empty ? 'var(--warn)' : 'var(--muted)' }}>
          {empty ? `nothing matches “${value.trim()}”` : `${shown} of ${total}`}
        </span>
      ) : null}
    </div>
  )
}
